import React, { useMemo, useState } from 'react'
import classnames from 'classnames'
import { date } from '../../format'
import MoneyCell from '../../components/MoneyCell'
import SignedMoneyCell from '../../components/SignedMoneyCell'
import SignedPercentCell from '../../components/SignedPercentCell'
import type { VarianceRow } from '../../saber.types'
import { GroupChevron, GroupViewToolbar, GROUP_SUB_ROW_CLASS, SortableTh, nextSort, sortRows } from './grouped'
import type { GroupSort, GroupView, SortValue } from './grouped'
import { categoryLabel, categoryTooltip, displayTenantName } from './labels'
import { CELL_COLOR } from '../../cellColor'
import { columnHelp } from '../../columnHelp'
import { tableBodyRowClass } from '../../tableRowStyles'

// Variance — Invoices as a grouped-by-tenant table (§8): tenant rollup rows
// (expandable to tinted invoice sub-rows) with a "Group by tenant" toggle.
// 6 sortable columns; row click opens the variance detail drawer.

const PAD = 'px-3 py-2'

interface VarianceGroup {
  tenantCuid: string
  tenantName: string
  rows: VarianceRow[]
  totalVariance: number
  /** Summed actual (invoice) and saber (shadow) gross revenue — the two amounts the
      group's Total Variance is the difference of. */
  actualGrossRevenue: number
  saberGrossRevenue: number
  /** Worst (most negative) invoice variance % in the group. */
  worstPct: number
  materialCount: number
}

const groupVariance = (rows: VarianceRow[]): VarianceGroup[] => {
  const byTenant = new Map<string, VarianceGroup>()
  const out: VarianceGroup[] = []
  for (const row of rows) {
    let group = byTenant.get(row.tenantCuid)
    if (group == null) {
      group = {
        tenantCuid: row.tenantCuid,
        tenantName: row.tenantName,
        rows: [],
        totalVariance: 0,
        actualGrossRevenue: 0,
        saberGrossRevenue: 0,
        worstPct: Number.POSITIVE_INFINITY,
        materialCount: 0
      }
      byTenant.set(row.tenantCuid, group)
      out.push(group)
    }
    group.rows.push(row)
    group.totalVariance += row.totalVariance
    group.actualGrossRevenue += row.actualGrossRevenue ?? 0
    group.saberGrossRevenue += row.saberGrossRevenue ?? 0
    group.worstPct = Math.min(group.worstPct, row.variancePct)
    if (row.isMaterial) group.materialCount += 1
  }
  return out
}

interface ColumnDef {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  groupValue: (g: VarianceGroup) => SortValue
  rowValue: (r: VarianceRow) => SortValue
}

const COLUMNS: ColumnDef[] = [
  { key: 'tenant', label: 'Tenant', groupValue: (g) => g.tenantName, rowValue: (r) => r.tenantName },
  {
    key: 'invoice',
    label: 'Invoice',
    groupValue: (g) => g.rows.length,
    rowValue: (r) => r.invoiceNumber
  },
  // The two amounts the Total Variance is the difference of (saber − actual), so a
  // reviewer can see the numbers behind the variance.
  { key: 'invoiceAmt', label: 'Invoice $', align: 'right', groupValue: (g) => g.actualGrossRevenue, rowValue: (r) => r.actualGrossRevenue },
  { key: 'shadowAmt', label: 'Shadow $', align: 'right', groupValue: (g) => g.saberGrossRevenue, rowValue: (r) => r.saberGrossRevenue },
  { key: 'variance', label: 'Total Variance $', align: 'right', groupValue: (g) => g.totalVariance, rowValue: (r) => r.totalVariance },
  { key: 'pct', label: 'Variance %', align: 'right', groupValue: (g) => g.worstPct, rowValue: (r) => r.variancePct },
  {
    key: 'driver',
    label: 'Variance Driver',
    align: 'center',
    // WEB-6568 §2f: groups sort by how many invoices are material; flat rows
    // sort material-first, then by driver code.
    groupValue: (g) => g.materialCount,
    rowValue: (r) => `${r.isMaterial ? 0 : 1}-${r.varianceCategory}`
  }
]

// WEB-6568 §2f: a variance counts as material above the backend's $1.00
// MATERIALITY_THRESHOLD (saber-layer-45-variance).
const MATERIALITY_USD = 1

// WEB-6568 §2f: a single combined pill replacing the old Category + Material
// columns. Materiality is the prominence signal — a material variance shows a
// solid red driver pill, an immaterial one a muted grey pill — so the driver
// (kWh / Rate / Allocation / Discount / Mixed) reads at a glance without the
// old, misleading "HIGH" badge or a separate column.
const DriverPill: React.FC<{ category: string; material: boolean }> = ({ category, material }) => (
  <span
    title={categoryTooltip(category)}
    className={classnames(
      'inline-flex items-center whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset',
      material
        ? 'bg-red-100 text-red-800 ring-red-600/20 dark:bg-red-400/10 dark:text-red-400 dark:ring-red-400/50'
        : 'bg-slate-100 text-slate-500 ring-slate-500/20 dark:bg-slate-700/40 dark:text-slate-400 dark:ring-slate-600/40'
    )}
  >
    {categoryLabel(category)}
  </span>
)

// WEB-6568 §2f: the "N material" companion chip on a rollup row — how many of a
// tenant's invoices individually exceed the threshold. Useful when the net
// rolls up immaterial because individual invoices offset each other.
const MaterialCountChip: React.FC<{ count: number; total: number }> = ({ count, total }) => (
  <span
    className={classnames('whitespace-nowrap text-[11px] font-medium', CELL_COLOR.negative)}
    title={`${count} of ${total} invoices exceed the $${MATERIALITY_USD} materiality threshold`}
  >
    · {count} material
  </span>
)

// WEB-6568: lead with the human invoice number, billing window as muted context.
// When the invoice has no servicing.invoice row the number is null — show the
// billing window as the identifier instead, NEVER the raw cuid.
const invoiceCell = (r: VarianceRow, indent: boolean): React.ReactNode => {
  const billingWindow = `${date(r.billingStartDate)} – ${date(r.billingEndDate)}`
  const hasNumber = r.invoiceNumber != null && r.invoiceNumber !== ''
  return (
    <td className={classnames(PAD, indent && 'pl-10')}>
      <span className="inline-flex items-center gap-2">
        {indent && <span className={classnames('text-xs', CELL_COLOR.null)}>└</span>}
        {hasNumber ? (
          <>
            <span className={classnames('whitespace-nowrap font-medium', CELL_COLOR.secondary)}>{r.invoiceNumber}</span>
            <span className={classnames('whitespace-nowrap text-[11px]', CELL_COLOR.null)}>{billingWindow}</span>
          </>
        ) : (
          <span className={classnames('whitespace-nowrap', CELL_COLOR.secondary)}>{billingWindow}</span>
        )}
      </span>
    </td>
  )
}

// The Invoice $ / Shadow $ pair — the two gross amounts the Total Variance is the
// difference of (saber − actual). Rendered just before the variance so the row reads
// invoice → shadow → their difference.
const amountCells = (r: VarianceRow): React.ReactNode => (
  <>
    <td className={classnames(PAD, 'text-right', CELL_COLOR.muted)}>
      <MoneyCell value={r.actualGrossRevenue} role="money" />
    </td>
    <td className={classnames(PAD, 'text-right', CELL_COLOR.muted)}>
      <MoneyCell value={r.saberGrossRevenue} role="money" />
    </td>
  </>
)

const tailCells = (r: VarianceRow): React.ReactNode => (
  <>
    <td className={classnames(PAD, 'text-right')}>
      <SignedMoneyCell value={r.totalVariance} role="variance" valueClassName="font-medium" />
    </td>
    <td className={classnames(PAD, 'text-right text-slate-700 dark:text-slate-200')}>
      <SignedPercentCell value={r.variancePct} />
    </td>
    <td className={classnames(PAD, 'text-center')}>
      <DriverPill category={r.varianceCategory} material={r.isMaterial} />
    </td>
  </>
)

export interface VarianceSelection {
  invoiceCuid: string
  meterServiceCuid: string
}

export interface GroupedVarianceTableProps {
  rows: VarianceRow[]
  /** saber_45 grain is (invoice, meter) — both cuids identify the clicked row. */
  onSelect: (selection: VarianceSelection) => void
  /** Selected property name — strips the duplicated suffix from tenant names (WEB-6568). */
  propertyName?: string
  /** Extra toolbar content (e.g. the category filter bar). */
  actions?: React.ReactNode
}

export const GroupedVarianceTable: React.FC<GroupedVarianceTableProps> = ({ rows, onSelect, propertyName, actions }) => {
  const [view, setView] = useState<GroupView>('tenant')
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const [sort, setSort] = useState<GroupSort>({ key: null, direction: 'asc' })

  const groups = useMemo(() => groupVariance(rows), [rows])
  const multiKeys = useMemo(() => groups.filter((g) => g.rows.length > 1).map((g) => g.tenantCuid), [groups])
  const allOpen = multiKeys.length > 0 && multiKeys.every((k) => expanded.has(k))

  const toggle = (key: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const sortCol = COLUMNS.find((c) => c.key === sort.key)
  const sortedGroups = useMemo(() => sortRows(groups, sort, sortCol?.groupValue), [groups, sort, sortCol])
  const sortedFlat = useMemo(() => sortRows(rows, sort, sortCol?.rowValue), [rows, sort, sortCol])

  return (
    <div>
      <GroupViewToolbar
        view={view}
        onViewChange={setView}
        canExpand={multiKeys.length > 0}
        allOpen={allOpen}
        onToggleAll={() => setExpanded(allOpen ? new Set() : new Set(multiKeys))}
        ariaLabel="Variance table view"
        actions={actions}
      />
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <SortableTh
                  key={c.key}
                  label={c.label}
                  align={c.align}
                  active={sort.key === c.key}
                  direction={sort.direction}
                  onClick={() => setSort((prev) => nextSort(prev, c.key))}
                  title={columnHelp('variance', c.key)}
                />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-6 text-center text-slate-400 dark:text-slate-500">
                  No variance rows for this period.
                </td>
              </tr>
            ) : view === 'flat' ? (
              sortedFlat.map((r, index) => (
                <tr
                  key={`${r.invoiceCuid}::${r.meterServiceCuid}`}
                  onClick={() => onSelect({ invoiceCuid: r.invoiceCuid, meterServiceCuid: r.meterServiceCuid })}
                  className={tableBodyRowClass(index, 'cursor-pointer')}
                >
                  <td className={classnames(PAD, 'text-slate-700 dark:text-slate-200')}>
                    {displayTenantName(r.tenantName, propertyName)}
                  </td>
                  {invoiceCell(r, false)}
                  {amountCells(r)}
                  {tailCells(r)}
                </tr>
              ))
            ) : (
              (() => {
                let rowIndex = 0
                return sortedGroups.flatMap((g) => {
                const multi = g.rows.length > 1
                const open = expanded.has(g.tenantCuid)
                const groupIndex = rowIndex++
                // Distinct drivers among the group's material invoices (headline pill when net is material).
                const materialDrivers = [...new Set(g.rows.filter((r) => r.isMaterial).map((r) => r.varianceCategory))]
                const out = [
                  <tr
                    key={g.tenantCuid}
                    onClick={() =>
                      multi
                        ? toggle(g.tenantCuid)
                        : onSelect({ invoiceCuid: g.rows[0].invoiceCuid, meterServiceCuid: g.rows[0].meterServiceCuid })
                    }
                    className={tableBodyRowClass(groupIndex, 'cursor-pointer')}
                  >
                    <td className={PAD}>
                      <span className="inline-flex items-center gap-1.5">
                        {multi ? <GroupChevron open={open} /> : <span className="inline-block w-3" />}
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                          {displayTenantName(g.tenantName, propertyName)}
                        </span>
                      </span>
                    </td>
                    {multi ? (
                      <td className={classnames(PAD, 'text-xs', CELL_COLOR.null)}>
                        {open ? '' : `${g.rows.length} invoices`}
                      </td>
                    ) : (
                      invoiceCell(g.rows[0], false)
                    )}
                    <td className={classnames(PAD, 'text-right', CELL_COLOR.muted)}>
                      <MoneyCell value={g.actualGrossRevenue} role="money" />
                    </td>
                    <td className={classnames(PAD, 'text-right', CELL_COLOR.muted)}>
                      <MoneyCell value={g.saberGrossRevenue} role="money" />
                    </td>
                    <td className={classnames(PAD, 'text-right')}>
                      <SignedMoneyCell
                        value={g.totalVariance}
                        role="variance"
                        valueClassName="font-semibold"
                      />
                    </td>
                    <td
                      className={classnames(PAD, 'text-right', CELL_COLOR.muted)}
                      title={multi ? 'worst invoice in the group' : undefined}
                    >
                      <SignedPercentCell value={g.worstPct} />
                    </td>
                    <td className={classnames(PAD, 'text-center')}>
                      {multi ? (
                        <span className="inline-flex flex-wrap items-center justify-center gap-1">
                          {/* Headline pill reflects the tenant's NET position: driver(s) when the
                              net variance is material, a muted Immaterial pill when it nets out. */}
                          {Math.abs(g.totalVariance) > MATERIALITY_USD && materialDrivers.length > 0 ? (
                            <>
                              {materialDrivers.slice(0, 2).map((c) => (
                                <DriverPill key={c} category={c} material />
                              ))}
                              {materialDrivers.length > 2 && (
                                <span className="text-xs text-slate-400 dark:text-slate-500">
                                  +{materialDrivers.length - 2}
                                </span>
                              )}
                            </>
                          ) : (
                            <DriverPill category="IMMATERIAL" material={false} />
                          )}
                          {g.materialCount > 0 && <MaterialCountChip count={g.materialCount} total={g.rows.length} />}
                        </span>
                      ) : (
                        <DriverPill category={g.rows[0].varianceCategory} material={g.rows[0].isMaterial} />
                      )}
                    </td>
                  </tr>
                ]
                if (multi && open) {
                  for (const r of g.rows) {
                    out.push(
                      <tr
                        key={`${r.invoiceCuid}::${r.meterServiceCuid}`}
                        onClick={() => onSelect({ invoiceCuid: r.invoiceCuid, meterServiceCuid: r.meterServiceCuid })}
                        className={classnames('cursor-pointer', GROUP_SUB_ROW_CLASS)}
                      >
                        <td className={PAD} />
                        {invoiceCell(r, true)}
                        {amountCells(r)}
                        {tailCells(r)}
                      </tr>
                    )
                  }
                }
                return out
              })
              })()
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default GroupedVarianceTable
